import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, Building2, Plus } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useTenant, type Tenant } from '../providers/TenantProvider';
import { Input } from './ui/input';

export function TenantSwitcher() {
  const { t } = useTranslation();
  const { currentTenant, tenants, switchTenant } = useTenant();
  const [search, setSearch] = useState('');

  const filteredTenants = tenants.filter(
    (tenant) =>
      tenant.name.toLowerCase().includes(search.toLowerCase()) ||
      tenant.slug.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (tenant: Tenant) => {
    switchTenant(tenant.id);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="w-full justify-between px-2 h-auto py-2">
          <div className="flex items-center gap-2 truncate">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <Building2 className="h-4 w-4 text-primary" />
            </div>
            <div className="flex flex-col items-start truncate">
              <span className="text-sm font-medium truncate">
                {currentTenant?.name || 'Select Organization'}
              </span>
              <span className="text-xs text-muted-foreground truncate">
                {currentTenant?.plan || ''}
              </span>
            </div>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64" align="start">
        <DropdownMenuLabel>{t('tenantSwitcher.title')}</DropdownMenuLabel>
        <div className="p-2">
          <Input
            placeholder={t('tenantSwitcher.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8"
          />
        </div>
        <DropdownMenuSeparator />
        <div className="max-h-64 overflow-auto">
          {filteredTenants.length === 0 ? (
            <div className="p-2 text-center text-sm text-muted-foreground">
              {t('tenantSwitcher.noOrganizations')}
            </div>
          ) : (
            filteredTenants.map((tenant) => (
              <DropdownMenuItem
                key={tenant.id}
                onClick={() => handleSelect(tenant)}
                className="cursor-pointer"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 mr-2">
                  <Building2 className="h-4 w-4 text-primary" />
                </div>
                <div className="flex flex-col flex-1 truncate">
                  <span className="truncate">{tenant.name}</span>
                  <span className="text-xs text-muted-foreground capitalize">{tenant.role}</span>
                </div>
                {currentTenant?.id === tenant.id && <Check className="ml-2 h-4 w-4 text-primary" />}
              </DropdownMenuItem>
            ))
          )}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer">
          <Plus className="mr-2 h-4 w-4" />
          {t('tenantSwitcher.createOrganization')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
